import { useState, useRef, useCallback, useEffect } from 'react'
import abcjs from 'abcjs'
import type { StepDirection } from '@/lib/types'

export interface PlaybackSpec {
  abcSource: string
  referenceMidi: number
  rangeLowMidi: number
  rangeHighMidi: number
  stepSemitones: number
  direction: StepDirection
  tempoQpm: number
}

/**
 * The pattern is written once in its reference key; playback marches it
 * through the singer's range by semitone offsets. UP_DOWN climbs to the top
 * of the range and walks back down without repeating the peak.
 */
export function computeOffsets(spec: PlaybackSpec): number[] {
  const low = spec.rangeLowMidi - spec.referenceMidi
  const high = spec.rangeHighMidi - spec.referenceMidi
  const step = Math.max(1, spec.stepSemitones || 1)
  const ascending: number[] = []
  for (let o = low; o <= high; o += step) ascending.push(o)
  if (ascending.length === 0) ascending.push(low)
  if (spec.direction === 'DOWN') return [...ascending].reverse()
  if (spec.direction === 'UP_DOWN') return [...ascending, ...ascending.slice(0, -1).reverse()]
  return ascending
}

// One AudioContext for the whole app, created inside a user gesture (mobile
// browsers refuse otherwise).
let sharedAudioContext: AudioContext | null = null
function getAudioContext(): AudioContext {
  if (!sharedAudioContext) {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext
    sharedAudioContext = new Ctor()
  }
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume()
  }
  return sharedAudioContext
}

export function useWarmupPlayback(spec: PlaybackSpec | null) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [stepIndex, setStepIndex] = useState(-1)
  const [currentOffset, setCurrentOffset] = useState(0)
  // User adjustments: shift the whole range up/down; scale the tempo
  const [nudge, setNudge] = useState(0)
  const [tempoQpm, setTempoQpm] = useState(spec?.tempoQpm ?? 90)

  const cancelRef = useRef(false)
  const synthRef = useRef<any>(null)
  const endedResolveRef = useRef<(() => void) | null>(null)
  const nudgeRef = useRef(0)
  const tempoRef = useRef(tempoQpm)
  nudgeRef.current = nudge
  tempoRef.current = tempoQpm

  const totalSteps = spec ? computeOffsets(spec).length : 0

  // New exercise in the slot: halt audio, reset per-exercise state
  useEffect(() => {
    cancelRef.current = true
    try { synthRef.current?.stop() } catch {}
    endedResolveRef.current?.()
    setIsPlaying(false)
    setIsPaused(false)
    setStepIndex(-1)
    setCurrentOffset(0)
    setNudge(0)
    setTempoQpm(spec?.tempoQpm ?? 90)
  }, [spec?.abcSource])

  useEffect(() => () => {
    cancelRef.current = true
    try { synthRef.current?.stop() } catch {}
  }, [])

  const playStep = useCallback(async (abc: string, offset: number, qpm: number) => {
    const visualObj = abcjs.renderAbc('*', abc, { visualTranspose: offset })[0]
    const synth = new abcjs.synth.CreateSynth()
    synthRef.current = synth

    let resolveEnded!: () => void
    const ended = new Promise<void>((r) => { resolveEnded = r })
    endedResolveRef.current = resolveEnded

    await synth.init({
      visualObj,
      audioContext: getAudioContext(),
      options: { qpm, onEnded: resolveEnded },
    })
    await synth.prime()
    if (cancelRef.current) return
    synth.start()

    // onEnded is the real signal; the timer is a jam-release in case it never fires
    let capMs = 60000
    try {
      visualObj.setTiming(qpm, 0)
      const total = (visualObj as any).getTotalTime?.()
      if (total) capMs = total * 1000 * 1.5 + 2000
    } catch {}
    await Promise.race([ended, new Promise((r) => setTimeout(r, capMs))])
  }, [])

  const play = useCallback(async () => {
    if (!spec || isPlaying) return
    getAudioContext() // create/unlock inside the tap
    cancelRef.current = false
    setIsPlaying(true)
    setIsPaused(false)
    try {
      const baseOffsets = computeOffsets(spec)
      for (let i = 0; i < baseOffsets.length; i++) {
        if (cancelRef.current) break
        const offset = baseOffsets[i] + nudgeRef.current
        setStepIndex(i)
        setCurrentOffset(offset)
        await playStep(spec.abcSource, offset, tempoRef.current)
        // brief breath between steps
        if (!cancelRef.current) await new Promise((r) => setTimeout(r, 350))
      }
    } finally {
      setIsPlaying(false)
      setIsPaused(false)
      setStepIndex(-1)
    }
  }, [spec, isPlaying, playStep])

  const stop = useCallback(() => {
    cancelRef.current = true
    try { synthRef.current?.stop() } catch {}
    endedResolveRef.current?.()
    setIsPaused(false)
  }, [])

  const togglePause = useCallback(() => {
    const synth = synthRef.current
    if (!synth || !isPlaying) return
    if (isPaused) {
      try { synth.resume() } catch {}
      setIsPaused(false)
    } else {
      try { synth.pause() } catch {}
      setIsPaused(true)
    }
  }, [isPlaying, isPaused])

  return {
    isPlaying,
    isPaused,
    stepIndex,
    totalSteps,
    /** Offset the visible engraving should use right now (climbs during playback). */
    displayOffset: isPlaying ? currentOffset : nudge,
    nudge,
    setNudge,
    tempoQpm,
    setTempoQpm,
    play,
    stop,
    togglePause,
  }
}
